// Interior Modeling Pricing — single source of truth for tiered rate logic.
// NOT graduated: one rate is selected from the combined total interior SF and applied to ALL of it.

export const INTERIOR_MODELING_TIERS = [
  { max: 2500,   rate: 1.75, label: "0–2,500 SF" },
  { max: 5000,   rate: 1.50, label: "2,501–5,000 SF" },
  { max: 10000,  rate: 1.25, label: "5,001–10,000 SF" },
  { max: 20000,  rate: 1.00, label: "10,001–20,000 SF" },
  { max: 35000,  rate: 0.85, label: "20,001–35,000 SF" },
  { max: Infinity, rate: 0.75, label: "35,001+ SF" }
];

/**
 * Normalize admin tier rules from the backend into the tier shape used locally.
 * Accepts an array of pricing_rules docs (calculator_type=interior_modeling).
 * Returns the sorted tier array or falls back to the default constants.
 */
export function normalizeTiers(adminRules) {
  if (!Array.isArray(adminRules) || adminRules.length === 0) return INTERIOR_MODELING_TIERS;
  const parsed = adminRules
    .filter(r => r.active !== false)
    .map(r => ({
      max: (r.tier_config?.max_sqft ?? 999999999) >= 999999999 ? Infinity : Number(r.tier_config?.max_sqft),
      min: Number(r.tier_config?.min_sqft ?? 0),
      rate: Number(r.default_rate),
      label: r.label || ""
    }))
    .filter(t => Number.isFinite(t.rate) && (Number.isFinite(t.max) || t.max === Infinity))
    .sort((a, b) => (a.max === Infinity ? 1 : b.max === Infinity ? -1 : a.max - b.max));
  return parsed.length > 0 ? parsed : INTERIOR_MODELING_TIERS;
}

/**
 * Select the interior modeling rate for a given combined total SF.
 * @param {number} totalInteriorSqft - residential + amenity interior SF
 * @param {Array}  [tiers] - optional override tier table
 * @returns {{rate:number, tierLabel:string}}
 */
export function getInteriorModelingRate(totalInteriorSqft, tiers = INTERIOR_MODELING_TIERS) {
  const sqft = Math.max(0, Number(totalInteriorSqft) || 0);
  const tier = tiers.find(t => sqft <= t.max) || tiers[tiers.length - 1];
  return { rate: tier.rate, tierLabel: tier.label };
}

/**
 * Compute the full interior modeling breakdown.
 * Applies the SAME selected tier rate to both residential and amenity interior SF.
 * Amenity entries of type "standalone_structure" still get their structure fee (charged once per sharedGroupId).
 *
 * @param {object} input
 * @param {number} input.residentialSqft - residential interior SF (e.g. interiorSqft)
 * @param {Array}  input.amenityEntries  - [{id, sqft, modelingType, structureSize, sharedGroupId, ...}]
 * @returns {object} detailed breakdown
 */
export function calculateInteriorModeling({ residentialSqft = 0, amenityEntries = [], tiers } = {}) {
  const R = Math.max(0, Number(residentialSqft) || 0);
  const amenitySqftTotal = amenityEntries.reduce((sum, a) => sum + (Number(a.sqft) || 0), 0);
  const A = amenitySqftTotal;
  const T = R + A;

  const { rate, tierLabel } = getInteriorModelingRate(T, tiers || INTERIOR_MODELING_TIERS);

  const residentialModelingCost = Math.round(R * rate);

  // Structure fee logic stays for standalone amenities (not tied to sqft rate)
  const structureSizes = { small: 1000, medium: 2000, large: 3500, xl: 5000 };
  const chargedGroups = new Set();

  const amenityDetails = amenityEntries.map(a => {
    const sqft = Number(a.sqft) || 0;
    const interiorCost = Math.round(sqft * rate);
    let structureFee = 0;
    let structureCharged = false;
    if (a.modelingType === "standalone_structure") {
      const groupKey = (a.sharedGroupId && String(a.sharedGroupId).trim()) || a.id;
      if (!chargedGroups.has(groupKey)) {
        structureFee = structureSizes[a.structureSize] || 2000;
        chargedGroups.add(groupKey);
        structureCharged = true;
      }
    }
    return {
      ...a,
      interiorCost,           // sqft × tier rate
      structureFee,
      structureCharged,
      totalCost: interiorCost + structureFee,
      appliedRate: rate
    };
  });

  const amenityInteriorModelingCost = amenityDetails.reduce((sum, a) => sum + a.interiorCost, 0);
  const amenityStructureFeesTotal = amenityDetails.reduce((sum, a) => sum + a.structureFee, 0);
  const amenityModelingCost = amenityInteriorModelingCost + amenityStructureFeesTotal;

  const totalInteriorModelingFee = residentialModelingCost + amenityInteriorModelingCost;
  const interiorModelingSubtotal = totalInteriorModelingFee + amenityStructureFeesTotal;

  return {
    // Inputs echoed
    residentialSqft: R,
    amenitySqft: A,
    combinedInteriorSqft: T,
    // Rate selection
    appliedRate: rate,
    tierLabel,
    // Line item costs
    residentialModelingCost,          // R × rate
    amenityInteriorModelingCost,      // A × rate (sum of all amenity entries)
    amenityStructureFeesTotal,        // sum of standalone structure fees
    amenityModelingCost,              // amenity interior + structure fees
    amenityDetails,                   // per-entry breakdown
    totalInteriorModelingFee,         // (R + A) × rate, ignoring structure fees
    interiorModelingSubtotal          // (R + A) × rate + structure fees (use in section subtotal)
  };
}
